import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  CircleSlash,
  Clock,
  Info,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDateTime, formatUtc, humanise } from '@/lib/format';

/* ============================================================================================
   Surfaces
   ============================================================================================ */

export function Card({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border-subtle bg-surface shadow-xs',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 border-b border-border-subtle px-5 py-4',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="text-h3 text-text-primary">{title}</h2>
        {description && <p className="mt-0.5 text-small text-text-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/* ============================================================================================
   Status
   ============================================================================================ */

export type StatusTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

const TONES: Record<StatusTone, string> = {
  neutral: 'bg-surface-sunken text-text-secondary border-border-default',
  brand: 'bg-brand-subtle text-brand-text border-brand-border',
  success: 'bg-success-subtle text-success-text border-success-border',
  warning: 'bg-warning-subtle text-warning-text border-warning-border',
  danger: 'bg-danger-subtle text-danger-text border-danger-border',
  info: 'bg-info-subtle text-info-text border-info-border',
};

const TONE_ICONS: Record<StatusTone, typeof Info> = {
  neutral: CircleDashed,
  brand: CircleDashed,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  info: Info,
};

/**
 * A status chip.
 *
 * Always carries a label, and optionally an icon. Colour is never the only signal - a user who
 * cannot distinguish red from green still reads "Rejected".
 */
export function StatusBadge({
  tone = 'neutral',
  children,
  icon = true,
  className,
}: {
  tone?: StatusTone;
  children: ReactNode;
  icon?: boolean;
  className?: string;
}) {
  const Icon = TONE_ICONS[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-caption font-medium',
        TONES[tone],
        className,
      )}
    >
      {icon && <Icon aria-hidden className="size-3.5 shrink-0" />}
      {children}
    </span>
  );
}

/** Maps an asset lifecycle status to a tone. Kept in one place so it never drifts per screen. */
export function assetStatusTone(status: string | undefined): StatusTone {
  switch (status) {
    case 'DEPLOYED':
    case 'ACCEPTED':
      return 'success';
    case 'RESERVED':
      return 'info';
    case 'UNDER_REPAIR':
      return 'warning';
    case 'ACCEPTANCE_PENDING':
    case 'ASSIGNED':
      return 'info';
    case 'IT_PREPARATION':
    case 'AGENT_ENROLLED':
    case 'TAGGED':
    case 'REGISTERED':
    case 'PURCHASED':
      return 'brand';
    case 'READY_FOR_ASSIGNMENT':
      return 'success';
    case 'REJECTED':
    case 'LOST':
      return 'danger';
    case 'RETURNED':
      return 'warning';
    case 'RETIRED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function assignmentStatusTone(status: string | undefined): StatusTone {
  switch (status) {
    case 'ACCEPTED':
      return 'success';
    case 'PENDING_ACCEPTANCE':
      return 'warning';
    case 'REJECTED':
      return 'danger';
    case 'RETURNED':
      return 'info';
    case 'CANCELLED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function AssetStatusBadge({ status }: { status: string | undefined }) {
  return <StatusBadge tone={assetStatusTone(status)}>{humanise(status)}</StatusBadge>;
}

/* ============================================================================================
   Metrics
   ============================================================================================ */

export function MetricCard({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
  to,
  loading,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: StatusTone;
  icon?: ReactNode;
  /** When present the whole card is a link - the number is usually a question to drill into. */
  to?: string;
  loading?: boolean;
}) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className="text-label text-text-muted">{label}</span>
        {icon && (
          <span
            aria-hidden
            className={cn(
              'grid size-8 place-items-center rounded-lg border',
              TONES[tone],
            )}
          >
            {icon}
          </span>
        )}
      </div>
      {loading ? (
        <div className="mt-3 h-8 w-20 rounded-md bg-surface-sunken shimmer" />
      ) : (
        <div className="mt-2 text-display tabular text-text-primary">{value}</div>
      )}
      {hint && <p className="mt-1 text-caption text-text-muted">{hint}</p>}
    </>
  );

  const className = cn(
    'block rounded-xl border border-border-subtle bg-surface p-5 shadow-xs transition-shadow',
    to && 'hover:border-border-default hover:shadow-md',
  );

  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

/* ============================================================================================
   Key/value display
   ============================================================================================ */

export function DescriptionList({
  items,
  columns = 2,
  className,
}: {
  items: { label: string; value: ReactNode }[];
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  return (
    <dl
      className={cn(
        'grid gap-x-8 gap-y-4',
        columns === 1 && 'grid-cols-1',
        columns === 2 && 'grid-cols-1 sm:grid-cols-2',
        columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-label text-text-muted">{item.label}</dt>
          <dd className="mt-1 text-body text-text-primary break-words">{item.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ============================================================================================
   Timeline
   ============================================================================================ */

export interface TimelineEntry {
  id: string;
  title: ReactNode;
  timestamp?: string | null;
  actor?: ReactNode;
  description?: ReactNode;
  tone?: StatusTone;
  meta?: ReactNode;
}

/**
 * A vertical timeline for lifecycle history.
 *
 * Renders as an ordered list, so a screen reader announces position and count - a sequence of
 * `div`s would convey none of that.
 */
export function Timeline({ entries }: { entries: TimelineEntry[] }) {
  return (
    <ol className="relative space-y-0">
      {entries.map((entry, index) => {
        const tone = entry.tone ?? 'brand';
        const isLast = index === entries.length - 1;
        return (
          <li key={entry.id} className="relative flex gap-4 pb-6 last:pb-0">
            {!isLast && (
              <span
                aria-hidden
                className="absolute top-7 bottom-0 left-[0.6875rem] w-px bg-border-default"
              />
            )}
            <span
              aria-hidden
              className={cn(
                'relative z-10 mt-1 grid size-6 shrink-0 place-items-center rounded-full border-2 bg-surface',
                tone === 'success' && 'border-success',
                tone === 'danger' && 'border-danger',
                tone === 'warning' && 'border-warning',
                tone === 'info' && 'border-info',
                (tone === 'brand' || tone === 'neutral') && 'border-brand',
              )}
            >
              <span
                className={cn(
                  'size-2 rounded-full',
                  tone === 'success' && 'bg-success',
                  tone === 'danger' && 'bg-danger',
                  tone === 'warning' && 'bg-warning',
                  tone === 'info' && 'bg-info',
                  (tone === 'brand' || tone === 'neutral') && 'bg-brand',
                )}
              />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="text-h3 text-text-primary">{entry.title}</span>
                {entry.timestamp && (
                  <time
                    dateTime={entry.timestamp}
                    title={formatUtc(entry.timestamp)}
                    className="text-caption tabular text-text-muted"
                  >
                    {formatDateTime(entry.timestamp)}
                  </time>
                )}
              </div>
              {entry.actor && (
                <p className="mt-0.5 text-small text-text-secondary">{entry.actor}</p>
              )}
              {entry.description && (
                <p className="mt-1 text-small text-text-secondary">{entry.description}</p>
              )}
              {entry.meta && <div className="mt-2">{entry.meta}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ============================================================================================
   Misc
   ============================================================================================ */

/** A monospace identifier with a copy affordance, for tags, serials and request ids. */
export function Identifier({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value) return <span className="text-text-muted">—</span>;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md bg-surface-sunken px-1.5 py-0.5 font-mono text-caption text-text-secondary',
        className,
      )}
    >
      {value}
    </span>
  );
}

export function RelativeTime({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-text-muted">—</span>;
  return (
    <time dateTime={value} title={formatUtc(value)} className="tabular">
      {formatDateTime(value)}
    </time>
  );
}

export function PendingClock({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-caption text-text-muted">
      <Clock aria-hidden className="size-3.5" />
      {label}
    </span>
  );
}

export function NotAvailable({ reason }: { reason: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-caption text-text-muted">
      <CircleSlash aria-hidden className="size-3.5" />
      {reason}
    </span>
  );
}
